/**
 * Validation Utilities
 * 
 * This module provides validation functions for form inputs.
 * Used by React Hook Form and custom validation logic.
 */

import { ValidationRules, ValidationResult } from '../types/funnel.types';

// ============================================
// VALIDATION PATTERNS
// ============================================

/**
 * Email validation regex
 * Matches most valid email formats
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone number validation (US format)
 * Accepts various formats: (555) 123-4567, 555-123-4567, 5551234567
 */
const PHONE_REGEX = /^[\d\s\-().+]+$/;

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validate a value against a set of rules
 * Returns an object with valid status and optional error message
 * 
 * @param value - The value to validate
 * @param rules - Validation rules to apply
 * @returns ValidationResult with valid boolean and optional message
 */
export function validateField(
  value: unknown,
  rules?: ValidationRules
): ValidationResult {
  // If no rules, always valid
  if (!rules) {
    return { valid: true };
  }

  // Convert value to string for text-based validation
  const stringValue = value != null ? String(value) : '';

  // Required validation
  if (rules.required && !stringValue.trim()) {
    return {
      valid: false,
      message: 'This field is required',
    };
  }

  // Skip other validations if value is empty and not required
  if (!stringValue.trim() && !rules.required) {
    return { valid: true };
  }

  // Minimum length validation
  if (rules.minLength && stringValue.length < rules.minLength) {
    return {
      valid: false,
      message: `Please provide at least ${rules.minLength} characters`,
    };
  }

  // Maximum length validation
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    return {
      valid: false,
      message: `Please keep your response under ${rules.maxLength} characters`,
    };
  }

  // Email validation
  if (rules.email && !EMAIL_REGEX.test(stringValue)) {
    return {
      valid: false,
      message: 'Please enter a valid email address',
    };
  }

  // Custom pattern validation
  if (rules.pattern) {
    const regex = new RegExp(rules.pattern);
    if (!regex.test(stringValue)) {
      return {
        valid: false,
        message: rules.patternMessage || 'Please enter a valid value',
      };
    }
  }

  // All validations passed
  return { valid: true };
}

/**
 * Validate email format
 * Standalone function for common email validation
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Validate phone number format
 * Accepts various formats
 */
export function isValidPhone(phone: string): boolean {
  // Remove all formatting characters
  const digits = phone.replace(/\D/g, '');
  // Check for valid length (10 digits for US, or 11 with country code)
  return digits.length >= 10 && digits.length <= 11 && PHONE_REGEX.test(phone);
}

/**
 * Check if a value is empty
 * Handles strings, arrays, and objects
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Sanitize text input
 * Removes potentially harmful characters while preserving valid text
 */
export function sanitizeText(text: string): string {
  return text
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

/**
 * Format validation error for display
 * Makes error messages user-friendly
 */
export function formatValidationError(error: string): string {
  // Capitalize first letter
  return error.charAt(0).toUpperCase() + error.slice(1);
}

// ============================================
// REACT HOOK FORM VALIDATORS
// ============================================

/**
 * Create validators object for React Hook Form
 * Converts our ValidationRules to RHF format
 */
export function createFormValidators(rules?: ValidationRules) {
  if (!rules) return {};

  const validators: Record<string, unknown> = {};

  if (rules.required) {
    validators.required = 'This field is required';
  }

  if (rules.minLength) {
    validators.minLength = {
      value: rules.minLength,
      message: `Please provide at least ${rules.minLength} characters`,
    };
  }

  if (rules.maxLength) {
    validators.maxLength = {
      value: rules.maxLength,
      message: `Please keep your response under ${rules.maxLength} characters`,
    };
  }

  if (rules.email) {
    validators.pattern = {
      value: EMAIL_REGEX,
      message: 'Please enter a valid email address',
    };
  }

  if (rules.pattern) {
    validators.pattern = {
      value: new RegExp(rules.pattern),
      message: rules.patternMessage || 'Please enter a valid value',
    };
  }

  return validators;
}
